import { useState, useEffect, memo } from 'react'
import { useOutletContext } from 'react-router-dom'
import { Users, Car, UserPlus, Activity, AlertTriangle } from 'lucide-react'
import { getDashboardStats } from '../services/dashboardService.js'
import { formatDate } from '../utils/dates.js'
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts'

const COLORS_LIGHT = ['#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#ef4444', '#6366f1', '#14b8a6']
const COLORS_DARK = ['#60a5fa', '#a78bfa', '#f472b6', '#fbbf24', '#34d399', '#f87171', '#818cf8', '#2dd4bf']

const StatCard = memo(({ icon: Icon, label, value, color = 'blue' }) => {
  const colorMap = {
    blue: 'from-blue-500 to-blue-600',
    purple: 'from-purple-500 to-purple-600',
    green: 'from-green-500 to-green-600',
    amber: 'from-amber-500 to-amber-600',
  }
  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
          <p className="text-3xl font-bold mt-1">{value ?? '...'}</p>
        </div>
        <div className={`w-12 h-12 bg-gradient-to-r ${colorMap[color]} rounded-xl flex items-center justify-center`} aria-hidden="true">
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </div>
  )
})
StatCard.displayName = 'StatCard'

const Dashboard = () => {
  const { darkMode } = useOutletContext()
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const COLORS = darkMode ? COLORS_DARK : COLORS_LIGHT
  const axisColor = darkMode ? '#9ca3af' : '#6b7280'

  useEffect(() => {
    getDashboardStats()
      .then((res) => { setStats(res.data); setError(null) })
      .catch(() => setError('Failed to load dashboard statistics'))
      .finally(() => setLoading(false))
  }, [])

  if (error && !stats) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <AlertTriangle className="w-10 h-10 text-red-400 mb-3" />
        <p className="text-lg font-semibold text-red-400 mb-1">Failed to load dashboard</p>
        <p className="text-sm text-gray-400 mb-4">{error}</p>
        <button onClick={() => { setLoading(true); setError(null); getDashboardStats().then(r => setStats(r.data)).catch(() => setError('Failed to load dashboard statistics')).finally(() => setLoading(false)) }}
          className="px-4 py-2 bg-blue-500 text-white rounded-xl text-sm font-medium hover:bg-blue-600 transition-colors">
          Retry
        </button>
      </div>
    )
  }

  const fuelData = stats?.vehiclesByFuelType
    ? Object.entries(stats.vehiclesByFuelType).map(([name, value]) => ({ name, value }))
    : []

  const brandData = stats?.topBrands
    ? Object.entries(stats.topBrands).map(([name, count]) => ({ name, count }))
    : []

  const statusData = stats?.vehiclesByStatus
    ? Object.entries(stats.vehiclesByStatus).map(([name, value]) => ({ name, value }))
    : []

  const tooltipStyle = {
    backgroundColor: darkMode ? '#1f2937' : '#fff',
    border: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`,
    borderRadius: '0.75rem',
    color: darkMode ? '#fff' : '#111827',
  }

  return (
    <div className="space-y-6">
      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard icon={Users} label="Total Users" value={stats?.totalUsers?.toLocaleString()} color="blue" />
        <StatCard icon={UserPlus} label="New Users (30d)" value={stats?.newUsersLast30d?.toLocaleString()} color="purple" />
        <StatCard icon={Car} label="Total Vehicles" value={stats?.totalVehicles?.toLocaleString()} color="green" />
        <StatCard icon={Activity} label="Active Vehicles" value={stats?.activeVehicles?.toLocaleString()} color="amber" />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Vehicles by Fuel Type */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold mb-4">Vehicles by Fuel Type</h3>
          {fuelData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie data={fuelData} cx="50%" cy="50%" outerRadius={100} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                  {fuelData.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={tooltipStyle} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-gray-400">{loading ? 'Loading...' : 'No fuel type data available'}</div>
          )}
        </div>

        {/* Top Brands */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold mb-4">Top 10 Brands</h3>
          {brandData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={brandData} layout="vertical" margin={{ left: 80 }}>
                <XAxis type="number" tick={{ fill: axisColor, fontSize: 12 }} />
                <YAxis dataKey="name" type="category" width={75} tick={{ fill: axisColor, fontSize: 12 }} />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="count" fill={darkMode ? '#a78bfa' : '#8b5cf6'} radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-gray-400">{loading ? 'Loading...' : 'No brand data available'}</div>
          )}
        </div>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Vehicles by Status */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold mb-4">Vehicles by Status</h3>
          {statusData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie data={statusData} cx="50%" cy="50%" innerRadius={50} outerRadius={90} dataKey="value" label>
                  {statusData.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={tooltipStyle} />
                <Legend wrapperStyle={{ color: axisColor }} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[250px] flex items-center justify-center text-gray-400">{loading ? 'Loading...' : 'No status data available'}</div>
          )}
        </div>

        {/* Recent Registrations */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold mb-4">Recent Registrations</h3>
          <div className="space-y-3">
            {stats?.recentRegistrations?.length > 0 ? (
              stats.recentRegistrations.map((user, i) => (
                <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-gray-50 dark:bg-gray-700/50">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white text-sm font-bold" aria-label={`Avatar for ${user.name || user.email}`}>
                      {(user.name?.[0] || user.email?.[0] || '?').toUpperCase()}
                    </div>
                    <div>
                      <p className="text-sm font-medium">{user.name || 'No name'}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{user.email}</p>
                    </div>
                  </div>
                  <span className="text-xs text-gray-400">{formatDate(user.createdAt)}</span>
                </div>
              ))
            ) : (
              <div className="text-gray-400 text-center py-8">{loading ? 'Loading...' : 'No recent registrations'}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default Dashboard
